import { useState, type ReactElement } from 'react';
import { useWatch } from 'react-hook-form';
import type { FieldDefinition } from '@gr/core/content/entities/fields';
import type { PostFormatDto } from '@gr/core/content/posts/dto';
import { ErrorCode } from '@gr/core/errors';
import { useT } from '../../../../app/di';
import { Alert } from '../../../../ui-kit/Alert/Alert';
import { Button } from '../../../../ui-kit/Button/Button';
import { FormAlert, FormMarkdownField, FormSelectField, FormTextArea, FormTextField, useServerForm } from '../../../../ui-kit/form/form';
import type { ServerOutcome } from '../../../../ui-kit/form/server-errors';
import { FormFieldValues, type ReferenceLookup } from '../../entities';
import { POST_DRAFT_FIELDS, type PostDraft } from '../model/post-draft';
import { effectiveFormat, formatOptions } from '../model/post-formats';
import { PostFormatRules } from '../PostFormatRules/PostFormatRules';

/** Ответ сохранения: saved — черновик того, что сохранил сервер (теги нормализованы, POST-UC-2.CC-1). */
export type PostSaveOutcome = ServerOutcome & { saved: PostDraft | null };

export interface PostEditorProps {
  draft: PostDraft;
  /** Пользовательские поля постов блога (ENT-UC-4). */
  fields: readonly FieldDefinition[];
  /** Правила оформления блога (POST-UC-10). */
  formats: readonly PostFormatDto[];
  references: ReferenceLookup;
  /** Самый строгий лимит подписи активных каналов — счётчик символов. */
  captionLimit: number | null;
  editable: boolean;
  onSubmit(draft: PostDraft): Promise<PostSaveOutcome>;
}

/** POST-UC-2, POST-UC-11, POST-UC-12: название, правило оформления, текст, теги, поля и заметки поста. */
export function PostEditor({ draft, fields, formats, references, captionLimit, editable, onSubmit }: PostEditorProps): ReactElement {
  const { t } = useT();
  const [saved, setSaved] = useState(false);
  const { form, submit } = useServerForm<PostDraft, PostSaveOutcome>({
    defaultValues: draft,
    fields: POST_DRAFT_FIELDS,
    onSubmit: async (values) => {
      setSaved(false);
      const outcome = await onSubmit(values);
      setSaved(outcome.success);
      return outcome;
    },
    afterSuccess: (values, outcome) => outcome.saved ?? values,
  });
  const { control, formState } = form;
  const disabled = !editable || formState.isSubmitting;
  const formatId = useWatch({ control, name: 'formatId' });
  return (
    <form className="u-stack" onSubmit={submit} noValidate>
      {saved && !formState.isDirty ? <Alert tone="success">{t('posts.editor.saved')}</Alert> : null}
      <FormAlert form={form} />
      <FormTextField control={control} name="title" label={t('posts.editor.title')} rules={{ required: ErrorCode.FieldRequired }} disabled={disabled} />
      {formats.length > 0 ? (
        <FormSelectField
          control={control}
          name="formatId"
          label={t('posts.editor.format')}
          hint={t('posts.editor.formatHint')}
          options={formatOptions(formats, (name) => (name ? t('posts.editor.formatDefault', { name }) : t('posts.editor.formatDefaultNone')))}
          disabled={disabled}
        />
      ) : null}
      <PostFormatRules format={effectiveFormat(formatId || null, formats)} byDefault={!formatId} />
      <FormTextArea control={control} name="body" label={t('posts.editor.body')} hint={t('posts.editor.bodyHint')} rows={10} maxCounter={captionLimit} disabled={disabled} />
      <FormTextField control={control} name="tags" label={t('posts.editor.tags')} hint={t('posts.editor.tagsHint')} disabled={disabled} />
      {fields.length > 0 ? <h3>{t('posts.editor.customFields')}</h3> : null}
      <FormFieldValues control={control} name="customValues" fields={fields} references={references} disabled={disabled} />
      <FormMarkdownField
        control={control}
        name="notes"
        label={t('posts.editor.notes')}
        hint={t('posts.editor.notesHint')}
        rows={4}
        previewLabel={t('posts.editor.preview')}
        emptyPreview={t('posts.editor.previewEmpty')}
        disabled={disabled}
      />
      {editable ? (
        <div className="u-row">
          <Button type="submit" variant="primary" loading={formState.isSubmitting} disabled={!formState.isDirty}>
            {t('posts.editor.save')}
          </Button>
          {formState.isDirty ? <span className="u-muted u-small">{t('posts.editor.unsaved')}</span> : null}
        </div>
      ) : null}
    </form>
  );
}
